import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';

// Several requests can fail together when a session dies; redirect once.
// The timestamp survives the round-trip through the login page (per tab):
// if the API still rejects the fresh session, the cause is not the session
// and bouncing through the login page again would only loop.
let redirectingToLogin = false;
const RELOGIN_AT_KEY = 'auth.relogin-at';
const RELOGIN_COOLDOWN_MS = 60 * 1000;

const redirectToLoginOnce = (loginUrl: string): void => {
    if (redirectingToLogin) {
        return;
    }
    let lastRelogin = 0;
    try {
        lastRelogin = Number(sessionStorage.getItem(RELOGIN_AT_KEY) ?? 0);
    } catch {
        // Storage can be unavailable in restricted browsing contexts.
    }
    if (Date.now() - lastRelogin < RELOGIN_COOLDOWN_MS) {
        return;
    }
    redirectingToLogin = true;
    try {
        sessionStorage.setItem(RELOGIN_AT_KEY, String(Date.now()));
    } catch {
        // Same as above; the in-memory flag still prevents a redirect storm.
    }
    window.location.href = loginUrl;
};

// Auth-central's own endpoints answer 401 as the normal "not signed in"
// signal, which the route guards already turn into a login redirect.
const isAuthCentralCall = (url: string): boolean => {
    try {
        return new URL(url, window.location.origin).pathname.startsWith('/auth/');
    } catch {
        return false;
    }
};

export const TokenInterceptor: HttpInterceptorFn = (req, next) => {
    const router = inject(Router);
    const authService = inject(AuthService);
    const activeBrandId = localStorage.getItem('activeBrandId');
    const userId = localStorage.getItem('userId');
    const userEmail = localStorage.getItem('userEmail');
    const userFirstName = localStorage.getItem('userFirstName');
    const userLastName = localStorage.getItem('userLastName');
    const isLocal = window.location.origin.startsWith('http://localhost:');

    const organizationId = localStorage.getItem('organizationId');
    const organizationName = localStorage.getItem('organizationName');

    const setHeaders: Record<string, string> = {};
    if (activeBrandId) {
        setHeaders['x-active-brand-id'] = activeBrandId;
    }
    if (organizationId) {
        setHeaders['x-organization-id'] = organizationId;
    }
    if (organizationName) {
        setHeaders['x-organization-name'] = organizationName;
    }

    if (isLocal && userId && userEmail) {
        setHeaders['x-dev-user-id'] = userId;
        setHeaders['x-dev-user-email'] = userEmail;
        setHeaders['x-dev-user-first-name'] = userFirstName ?? '';
        setHeaders['x-dev-user-last-name'] = userLastName ?? '';
    }

    const cloned = req.clone({
        withCredentials: true,
        setHeaders,
    });

    return next(cloned).pipe(
        catchError(error => {
            if (error.status === 401) {
                console.error('Unauthorized access');
                // The API rejected the session cookie (expired, revoked, or
                // no longer granting this app): go straight to auth-central.
                // The in-app /login route is not used here: its guard asks
                // /auth/verify, which may still accept a token the API has
                // already rejected, and would bounce back to the same page.
                if (!isAuthCentralCall(req.url)) {
                    redirectToLoginOnce(authService.loginUrl());
                }
            } else if (error.status === 403) {
                console.error('Forbidden access');
                void router.navigate(['/unauthorized']);
            } else if (error.status === 0) {
                console.error('Network error - please check your connection');
            } else {
                console.error(`Server error: ${error.status} ${error.message}`);
            }
            return throwError(() => error);
        })
    );
};